export const collectionsQuery = `#graphql
  query getCollections($first: Int!) {
    collections(first: $first) {
      edges {
        node {
          id
          title
          description
          handle
          image {
            url
            altText
          }
        }
      }
    }
  }
`;

// ✨  UPDATED – now accepts $sortKey, $reverse, and $language for translations
export const collectionProductsQuery = /* GraphQL */ `#graphql
  query getCollectionProducts(
    $id: ID!
    $first: Int!
    $after: String
    $filters: [ProductFilter!]
    $sortKey: ProductCollectionSortKeys
    $reverse: Boolean
    $language: LanguageCode
  ) @inContext(language: $language) {
    collection(id: $id) {
      id
      title
      description
      handle
      products(
        first: $first
        after: $after
        filters: $filters
        sortKey: $sortKey
        reverse: $reverse
      ) {
        edges {
          node {
            id
            title
            description
            handle
            availableForSale
            createdAt
            updatedAt
            productType
            vendor
            tags
            options { id name values }
            images(first: 5) {
              edges { node { id url altText width height } }
            }
            variants(first: 100) {
              edges {
                node {
                  id
                  title
                  price { amount currencyCode }
                  compareAtPrice { amount currencyCode }
                  availableForSale
                  quantityAvailable
                  selectedOptions { name value }
                }
              }
            }
          }
        }
        pageInfo { hasNextPage hasPreviousPage endCursor }
      }
    }
  }
`;

// Full product data query for prerender cache - fetches all fields needed by product detail pages
export const collectionProductsFullQuery = /* GraphQL */ `#graphql
  query getCollectionProductsFull(
    $id: ID!
    $first: Int!
    $after: String
    $language: LanguageCode
  ) @inContext(language: $language) {
    collection(id: $id) {
      id
      products(first: $first, after: $after) {
        edges {
          node {
            id
            title
            description
            descriptionHtml
            handle
            availableForSale
            createdAt
            updatedAt
            productType
            vendor
            tags
            seo {
              title
              description
            }
            options { id name values }
            images(first: 20) {
              edges { node { id url altText width height } }
            }
            variants(first: 100) {
              edges {
                node {
                  id
                  title
                  price { amount currencyCode }
                  compareAtPrice { amount currencyCode }
                  availableForSale
                  quantityAvailable
                  selectedOptions { name value }
                  image {
                    id
                    url
                    altText
                    width
                    height
                  }
                  availabilityTier: metafield(namespace: "custom", key: "availability_tier") {
                    value
                  }
                }
              }
            }
          }
        }
        pageInfo { hasNextPage endCursor }
      }
    }
  }
`;


export const productQuery = `#graphql
  query getProduct($handle: String!, $language: LanguageCode) @inContext(language: $language) {
    product(handle: $handle) {
      id
      title
      description
      descriptionHtml
      handle
      availableForSale
      createdAt
      updatedAt
      productType
      vendor
      tags
      seo {
        title
        description
      }
      images(first: 20) {
        edges {
          node {
            id
            url
            altText
            width
            height
          }
        }
      }
      variants(first: 100) {
        edges {
          node {
            id
            title
            price {
              amount
              currencyCode
            }
            compareAtPrice {
              amount
              currencyCode
            }
            availableForSale
            quantityAvailable
            selectedOptions {
              name
              value
            }
            image {
              id
              url
              altText
              width
              height
            }
            availabilityTier: metafield(namespace: "custom", key: "availability_tier") {
              value
            }
          }
        }
      }
      options {
        id
        name
        values
      }
    }
  }
`;


/* ───────── Search ──────────────────────────────────────────── */
export const searchProductsQuery = /* GraphQL */ `#graphql
  fragment ProductCardFields on Product {
    id
    title
    description
    handle
    availableForSale
    createdAt
    updatedAt
    productType
    vendor
    tags
    options { id name values }
    images(first: 5) {
      edges { node { id url altText width height } }
    }
    variants(first: 100) {
      edges {
        node {
          id
          title
          price { amount currencyCode }
          compareAtPrice { amount currencyCode }
          availableForSale
          quantityAvailable
          selectedOptions { name value }
        }
      }
    }
  }

  query searchProducts($query: String!, $first: Int!, $after: String, $language: LanguageCode) @inContext(language: $language) {
    products(first: $first, after: $after, query: $query) {
      edges {
        cursor
        node { ...ProductCardFields }
      }
      pageInfo { hasNextPage endCursor }
    }
  }
`;

export const getCollectionProductsSlugs = /* GraphQL */ `#graphql
  query GetCollectionProductSlugs($id: ID!, $first: Int!, $after: String) {
    collection(id: $id) {
      products(first: $first, after: $after) {
        edges {
          cursor
          node {
            id
            handle          # needed later
            title           # for the slug
            description     # for translation export
            availableForSale
          }
        }
        pageInfo { hasNextPage endCursor }
      }
    }
  }
`;

// Product recommendations query - returns related products based on Shopify's algorithm
export const productRecommendationsQuery = /* GraphQL */ `#graphql
  query getProductRecommendations($productId: ID!, $intent: ProductRecommendationIntent, $language: LanguageCode) @inContext(language: $language) {
    productRecommendations(productId: $productId, intent: $intent) {
      id
      title
      handle
      availableForSale
      images(first: 1) {
        edges { node { id url altText } }
      }
      variants(first: 1) {
        edges {
          node {
            id
            price { amount currencyCode }
            compareAtPrice { amount currencyCode }
            availableForSale
          }
        }
      }
    }
  }
`;